import { Avatar, Box, Container, Link, List, ListItem, ListItemAvatar, ListItemText, Typography, CircularProgress } from '@mui/material';
import SpeakerNotesOffOutlinedIcon from '@mui/icons-material/SpeakerNotesOffOutlined';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import QuestionMarkIcon from '@mui/icons-material/QuestionMark';
import { cardSX } from '../utilities/CSS';
import { useContext, useEffect, useState } from 'react';
import { UserContext } from '../contexts/UserContext';
import userService from '../services/UserService';
import { iUserServiceHistoryDTO } from '../utilities/APIInterfaces';
import { ServiceHistoryType, ServiceHistoryTypeLabel } from '../utilities/FormEnums';
import ConfirmationDialog from './ConfirmationDialog';

export default function Communications() {
  const userContext = useContext(UserContext);
  const [communications, setCommunications] = useState<iUserServiceHistoryDTO[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  
  type ViewMoreDialogOptionsType = {
    isOpen: boolean;
    isWorking: boolean;
    icon?: JSX.Element;
    date?: string;
    title?: string;
    sh?: iUserServiceHistoryDTO;
  };
  const [viewMoreDialogOptions, setViewMoreDialogOptions] = useState({} as ViewMoreDialogOptionsType);
  const [isViewMoreDataLoaded, setIsViewMoreDataLoaded] = useState(false);

  // Filter communications to only show emails
  const filteredCommunications = communications.filter(comm => comm.type === ServiceHistoryType.EMAIL);

  const handleViewMoreClick = (e: React.MouseEvent<HTMLAnchorElement> | React.KeyboardEvent<HTMLAnchorElement>) => {
    // Need id AND type of the service to find the correct service history item in the list (ids can overlap since multiple tables are involved)
    const a = (e.target as HTMLElement).closest('a')!;
    const id = a.getAttribute('data-view-more-id');
    const type = a.getAttribute('data-view-more-type');
    const sh = communications.find(sh => sh.id === Number(id) && sh.type === Number(type));

    if (sh) {
      const icon = <EmailOutlinedIcon />;
      let date = new Date(sh.action_Date ?? '').toLocaleString('en-US', {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      });
      date = date === 'Invalid Date' || isNaN(Date.parse(date)) ? 'Invalid Date' : date;

      const title = Object.values(ServiceHistoryType).includes(sh.type) ? `${ServiceHistoryTypeLabel.get(sh.type)} (${date})` : `Unknown Service (${date})`;

      setViewMoreDialogOptions({ icon, date, title, sh, isOpen: true, isWorking: false });

      if (sh.type === ServiceHistoryType.EMAIL) {
        setIsViewMoreDataLoaded(false);
        userService.GetUserServiceHistoryItem(userContext.user?.authId ?? '', sh.id, sh.type, (v) => v)
          .then(data => {
            setViewMoreDialogOptions({ icon, date, title, sh: data, isOpen: true, isWorking: false });
          }).finally(() => {
            setIsViewMoreDataLoaded(true);  
          });
      }
    }
  };

  useEffect(() => {
    if (userContext.user?.authId) {
      userService.GetUserMessageHistory(userContext.user.authId, setIsLoading)
        .then(result => {
          setCommunications(result);
        })
        .catch(error => {
          console.error('Error fetching communications:', error);
        })
        .finally(() => {
          setIsLoading(false);
        });
    }
  }, [userContext.user?.authId]);

  const LoadingState = () => {
    return (
      <>
        <Typography variant='h2' sx={{ fontSize: '1.5rem', fontWeight: 500 }}>Communications</Typography>
        <Box sx={cardSX.emptyStateBox}>
          <CircularProgress sx={{ color: '#eee', fontSize: '5.55rem', mb: 2 }} />
          <Typography variant='body2'>Loading communications...</Typography>
        </Box>
      </>
    );
  };

  const EmptyState = () => {
    return (
      <>
        <Typography variant='h2' sx={{ fontSize: '1.5rem', fontWeight: 500 }}>Communications</Typography>
        <Box sx={cardSX.emptyStateBox}>
          <SpeakerNotesOffOutlinedIcon sx={{ color: '#eee', fontSize: '5.55rem', mb: 2 }} />
          <Typography variant='body2'>No communications yet.</Typography>
        </Box>
      </>
    );
  };

  const CommunicationsList = () => {
    return (
      <>
        <Typography variant='h2' sx={{ fontSize: '1.5rem', fontWeight: 500, mb: 4.5 }}>Communications ({filteredCommunications.length})</Typography>
        <List
          sx={{
            // Want the "Communications" to never be taller than the 400px height in the Figma screens - set a max-height on the list.
            maxHeight: '245px',
            overflowY: 'auto',
            overscrollBehavior: 'contain',
            pt: 0,
            // Mac OS hiding scrollbars by default - overriding that (https://stackoverflow.com/questions/7492062/css-overflow-scroll-always-show-vertical-scroll-bar)
            // NOTE: This will affect Chrome on Windows, too.
            '&::-webkit-scrollbar': {
              WebkitAppearance: 'none',
              width: '4px',
            },
            '&::-webkit-scrollbar-thumb': {
              borderRadius: '4px',
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              boxShadow: '0 0 1px rgba(255, 255, 255, 0.5)'
            }
          }}
        >
          {filteredCommunications.map((sh, index) => {
            const icon = <EmailOutlinedIcon />;
            let date = new Date(sh.action_Date ?? '').toLocaleString('en-US', {
              weekday: 'short',
              month: 'short',
              day: 'numeric',
              year: 'numeric'
            });
            date = date === 'Invalid Date' || isNaN(Date.parse(date)) ? 'Invalid Date' : date;

            const title = Object.values(ServiceHistoryType).includes(sh.type) ? `${ServiceHistoryTypeLabel.get(sh.type)} (${date})` : `Unknown Service (${date})`;

            return (
              <ListItem key={index} alignItems="flex-start" sx={{ p: 0, mb: 1.75 }}>
                <ListItemAvatar
                  sx={{
                    mt: 0,
                    mr: 1.5,
                    minWidth: 'revert',
                    '& .MuiSvgIcon-root': {
                      width: '16px'
                    }
                  }}
                >{icon}</ListItemAvatar>
                <ListItemText 
                  sx={{ m: 0 }}
                  primary={
                    <Typography sx={{
                      display: 'flex',
                      alignItems: 'center',
                      fontSize: '0.875rem',
                      fontWeight:'bold',
                      mb: 0.5
                    }}>
                      {title}
                    </Typography>
                  }
                  secondary={
                    <Typography sx={{ display: 'flex', flexWrap: 'wrap', fontSize: '0.875rem', fontStyle: 'italic', pr: 1 }}>
                     <Box
                      component='span'
                      sx={{
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                        pr: 0.5
                      }}
                    >{sh.description}</Box>
                    <Link
                        data-view-more-id={sh.id}
                        data-view-more-type={sh.type}
                        tabIndex={0} // Need this to get it into the tab order since it doesn't have an href
                        sx={{
                          color: 'var(--link-color)',
                          cursor: 'pointer',
                          fontStyle: 'normal',
                          fontWeight: 'bold',
                          ml: 'auto',
                          textDecoration: 'none',
                          '&:hover, &:focus': {
                            textDecoration: 'underline'
                          }
                        }}
                        onClick={handleViewMoreClick}
                        onKeyDown={(e: React.KeyboardEvent<HTMLAnchorElement>) => {
                          // Keyboard accessibility - "click" link when pressing enter or space
                          if (e.key === 'Enter' || e.key === ' ') handleViewMoreClick(e);
                        }}
                      >View More</Link>
                    </Typography>
                  }
                />

              </ListItem>
            );
          })}
        </List>
      </>
    );
  };

  return (
    <Container sx={cardSX.container}>
      {isLoading && <LoadingState />}
      {!isLoading && filteredCommunications.length === 0 && <EmptyState />}
      {!isLoading && filteredCommunications.length > 0 && <CommunicationsList />}

      {/* View more dialog ------------------------------------------------------ */}
      {viewMoreDialogOptions.isOpen && <ConfirmationDialog
        ariaPrefix='view-more'
        dialogTitle='View Details'
        singleButton
        ctaButtonText='OK'
        ctaButtonWorkingText='Closing...'
        cancelButtonText='Cancel'
        open={viewMoreDialogOptions.isOpen}
        setOpenDialog={() => setViewMoreDialogOptions({ ...viewMoreDialogOptions, isOpen: false })}
        isWorking={viewMoreDialogOptions.isWorking}
        setIsWorking={() => setViewMoreDialogOptions({ ...viewMoreDialogOptions, isWorking: true })}
        width={528}
        dialogSX={{
          actions: { display: 'none' }
        }}
      >
        <Typography
          component='div'
          variant='body2'
          sx={{
            // Figma color (#b8b8b8) causes an accessibility contrast error - need to darken it
            color: '#757575',
            display: 'flex',
            alignItems: 'center',
            mb: 3
          }}
        >
          <Box sx={{
            display: 'flex',
            mr: 1.5,
            '& .MuiSvgIcon-root': {
              width: '16px',
              height: 'auto'
            }
          }}>{viewMoreDialogOptions.icon}</Box>
          {viewMoreDialogOptions.title}
        </Typography>

        {/* Don't want the dialog to be taller than 420px to match the Figma design */}
        <Box sx={{ maxHeight: 300, overflow: 'auto' }}>
          {/* Email has Subject/Message labels */}
          {viewMoreDialogOptions.sh?.type === ServiceHistoryType.EMAIL && (<>
            <Typography variant='body2' sx={{ mb: 2, textWrap: 'balance' }}>
              <strong>Subject: </strong>
              {viewMoreDialogOptions.sh.description}
            </Typography>
            { ! isViewMoreDataLoaded && (
              <Typography variant='body2' sx={{ mb: 0 }}>
                <strong>Message: </strong>
                Loading...
              </Typography>
            )}
            {isViewMoreDataLoaded && (
              <Typography variant='body2' sx={{ mb: 0 }}>
                <strong>Message: </strong>
                {viewMoreDialogOptions.sh.body}
              </Typography>
            )}
            {isViewMoreDataLoaded && viewMoreDialogOptions.sh?.type === ServiceHistoryType.EMAIL && (
              <Typography variant='body2' sx={{ mt: 2 }}>
                <strong>Recipients: </strong>
                {viewMoreDialogOptions.sh.recipient}
              </Typography>
            )}
          </>)}

          {/* Texts/Comments just display the content - no labels */}
          {viewMoreDialogOptions.sh?.type !== ServiceHistoryType.EMAIL && (
            <Typography variant='body2' sx={{ mb: 0, textWrap: 'balance' }}>
              {viewMoreDialogOptions.sh?.description}
            </Typography>
          )}
        </Box>
     </ConfirmationDialog>}

    </Container>
  );
}
